import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetList, useNotify, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getApiUrl, slotNameMap } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import { formatListDate } from "@/utils/date-format";
import { hallSlotMatchesRazaEventDate } from "@/utils/hall-slot-matches-raza-event-date";

type HallSlotRecord = {
  id: string;
  date: string | Date;
  slot: string;
  razaEventId: string | null;
  hall?: { name?: string | null; shortCode?: string | null } | null;
};

export type RazaRequestLinkHallSlotDialogProps = {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  /** The raza event id to link selected slots to. */
  razaEventId: string;
  /** Event calendar date — hall slots must match this day. */
  eventDate: string | Date;
};

function slotLabel(slot: string): string {
  return slotNameMap[slot] ?? slot;
}

function hallDisplay(h: HallSlotRecord): string {
  return h.hall?.name?.trim() || h.hall?.shortCode?.trim() || "—";
}

const RazaRequestLinkHallSlotDialog = ({
  open,
  onClose,
  bookingId,
  razaEventId,
  eventDate,
}: RazaRequestLinkHallSlotDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: allSlots = [], isLoading } = useGetList<HallSlotRecord>("hallBookings", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "date", order: "ASC" },
    filter: { bookingId },
  });

  const { linkableMatching, linkableMismatch, otherEventSlots } = useMemo(() => {
    const unlinked = allSlots.filter((s) => s.razaEventId == null || String(s.razaEventId) === "");
    return {
      linkableMatching: unlinked.filter((s) => hallSlotMatchesRazaEventDate(s.date, eventDate)),
      linkableMismatch: unlinked.filter((s) => !hallSlotMatchesRazaEventDate(s.date, eventDate)),
      otherEventSlots: allSlots.filter(
        (s) =>
          s.razaEventId != null &&
          String(s.razaEventId) !== "" &&
          String(s.razaEventId) !== razaEventId
      ),
    };
  }, [allSlots, eventDate, razaEventId]);

  // Reset selection when dialog re-opens
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [onClose, saving]);

  const handleToggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelected(new Set(linkableMatching.map((s) => s.id)));
  }, [linkableMatching]);

  const handleDeselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleLink = useCallback(async () => {
    if (selected.size === 0) {
      notify("Select at least one slot", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        [...selected].map((id) =>
          httpClient(`${getApiUrl()}/hallBookings/${id}`, {
            method: "PUT",
            body: JSON.stringify({ razaEventId }),
          })
        )
      );
      notify(
        selected.size === 1
          ? "Hall slot linked to this event"
          : `${selected.size} hall slots linked to this event`,
        { type: "success" }
      );
      refresh();
      handleClose();
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : "Failed to link slot(s)", { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [selected, razaEventId, notify, refresh, handleClose]);

  const allSelected = linkableMatching.length > 0 && selected.size === linkableMatching.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link hall slots to this event</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Only slots on {formatListDate(eventDate, { empty: "—", utc: true })} can be linked to
              this event.
            </Typography>

            {linkableMatching.length === 0 &&
            linkableMismatch.length === 0 &&
            otherEventSlots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hall slots found on this booking.
              </Typography>
            ) : null}

            {linkableMatching.length > 0 ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="subtitle2">Available slots</Typography>
                  <Button
                    size="small"
                    onClick={allSelected ? handleDeselectAll : handleSelectAll}
                    disabled={saving}
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </Button>
                </Stack>
                <Stack spacing={0.5}>
                  {linkableMatching.map((s) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.has(s.id)}
                          onChange={() => handleToggle(s.id)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {hallDisplay(s)} · {formatListDate(s.date, { empty: "—" })} ·{" "}
                          {slotLabel(s.slot)}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No unlinked slots match this event&apos;s date.
              </Typography>
            )}

            {linkableMismatch.length > 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {linkableMismatch.length === 1
                  ? "1 unlinked slot is on a different date and cannot be linked:"
                  : `${linkableMismatch.length} unlinked slots are on a different date and cannot be linked:`}
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {linkableMismatch.map((s) => (
                    <Typography key={s.id} variant="body2">
                      {hallDisplay(s)} · {formatListDate(s.date, { empty: "—" })} ·{" "}
                      {slotLabel(s.slot)}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            {otherEventSlots.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Already linked to another event
                </Typography>
                <Stack spacing={0.5}>
                  {otherEventSlots.map((s) => (
                    <Typography key={s.id} variant="body2" color="text.disabled">
                      {hallDisplay(s)} · {formatListDate(s.date, { empty: "—" })} ·{" "}
                      {slotLabel(s.slot)}
                    </Typography>
                  ))}
                </Stack>
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleLink()}
          variant="contained"
          disabled={saving || selected.size === 0 || isLoading}
        >
          {saving ? "Linking…" : `Link${selected.size > 0 ? ` (${selected.size})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RazaRequestLinkHallSlotDialog;
